import type { ICompletenessData } from 'legacy/lut/features/partner360/completeness'

/* Truncated rather than rounded, as the completeness detail reads it: 99.8% is not a finished plan. */
const toPercent = (total: number) => Math.floor(total * 100)

export interface INextIncompleteOffering {
  offering: string
  percent: number
}

interface IGetNextIncompleteOfferingParams {
  /** The partner's enabled offerings in the order the tabs list them. */
  offerings: string[]
  current: string | null
  profiles: ICompletenessData[]
}

/**
 * The offering a plan held by another one should be taken to: the first enabled offering after
 * the current one, in tab order and wrapping round, whose plan is short of 100%. An offering that
 * is itself incomplete is held by its own sections, so it leads nowhere else; nor does an offering
 * with no measured profile.
 */
export const getNextIncompleteOffering = ({
  offerings,
  current,
  profiles,
}: IGetNextIncompleteOfferingParams): INextIncompleteOffering | null => {
  const getPercent = (offering: string) => {
    const profile = profiles.find(({ profile: name }) => name?.trim() === offering)
    return profile ? toPercent(profile.total) : null
  }

  const currentPercent = current ? getPercent(current) : null

  if (currentPercent !== null && currentPercent < 100) {
    return null
  }

  const start = current ? offerings.indexOf(current) + 1 : 0
  const ordered = [...offerings.slice(start), ...offerings.slice(0, start)].filter(
    (offering) => offering !== current
  )

  for (const offering of ordered) {
    const percent = getPercent(offering)

    if (percent !== null && percent < 100) {
      return { offering, percent }
    }
  }

  return null
}
